import React, { useRef } from 'react';
import { RevenueRentalType } from 'types/TransactionReport';
import { SpotInterface } from 'types';
import { transactionReportsClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { useRevenueContext } from 'contexts/RevenueContext';
import { useToast } from 'contexts/ToastContext';
import {
    getAllTimeStartDate,
    getStartDateFromFilter,
    OnOffOption,
    Period,
    RevenueType,
    Timeframe,
} from 'components/RevenueWidgets/helpers';
import Modal, { ModalRef } from 'components/Modal/Modal';
import AGSimpleSelect, { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import PDFReportPreview from './PDFReportPreview/PDFReportPreview';
import CSVModal from './CSVModal';

type DownloadReportProps = {
    spot: SpotInterface;
    filters: {
        revenueType: RevenueType;
        timeframe: Timeframe;
        dailyPeriod: Period;
        rentalType: RevenueRentalType;
        yoyRevenueType: RevenueType;
        month: string;
        momYearCompare: OnOffOption;
        momGuaranteeCompare: OnOffOption;
    };
};

enum ExportType {
    CSV = 'csv',
    PDF = 'pdf',
    EXCEL = 'excel',
}

const exportTypeOptions: SelectOption[] = [
    {
        value: ExportType.CSV,
        label: 'as CSV',
    },
    {
        value: ExportType.PDF,
        label: 'as PDF',
    },
    {
        value: ExportType.EXCEL,
        label: 'as Excel',
    },
];

function DownloadReport({ spot, filters }: DownloadReportProps): JSX.Element {
    const csvDownloadModal = useRef<ModalRef>();
    const pdfDownloadModal = useRef<ModalRef>();
    const { addToast, dismissToast } = useToast();

    const { revenueByRentalTypeReport, pacingReport, yoyRevenueReport, momCurrentYearReport, dodReport } =
        useRevenueContext();
    const isDataReady = !!(
        revenueByRentalTypeReport &&
        pacingReport &&
        yoyRevenueReport &&
        (momCurrentYearReport || dodReport)
    );

    async function downloadExcel() {
        const loadingToastId = addToast({
            type: 'info',
            title: 'Generating Excel to download...',
            dismissible: false,
            timeout: 0,
        });

        try {
            const fileName = `${spot.name}_revenue.xlsx`.replace(/ /g, '-');
            const startDate = getStartDateFromFilter(filters.dailyPeriod);
            const momStartDate = filters.timeframe === Timeframe.ALL_TIME ? getAllTimeStartDate(spot) : undefined;
            await transactionReportsClient.getRevenueExcelReport(
                spot.uuid,
                fileName,
                filters.month,
                filters.rentalType,
                startDate,
                momStartDate
            );
            dismissToast(loadingToastId);
            addToast({
                type: 'success',
                title: 'Excel Downloaded!',
                dismissible: true,
                timeout: 3000,
            });
        } catch (e) {
            dismissToast(loadingToastId);
            const errorMessage = HTTPError.isHTTPError(e) ? e.message : e?.toString() || 'Something went wrong';
            addToast({
                type: 'error',
                title: errorMessage,
                dismissible: true,
                timeout: 5000,
            });
        }
    }

    function onSelectExportType(exportType: ExportType) {
        switch (exportType) {
            case ExportType.CSV:
                csvDownloadModal.current?.openModal();
                break;
            case ExportType.PDF:
                pdfDownloadModal.current?.openModal();
                break;
            case ExportType.EXCEL:
                downloadExcel();
                break;
        }
    }

    return (
        <div>
            <AGSimpleSelect
                placeholder="Export"
                options={exportTypeOptions}
                selected={''}
                onSelect={(value) => onSelectExportType(value as ExportType)}
                width="100px"
                disabled={!isDataReady}
            />

            {/* CSV Download modal */}
            <Modal ref={csvDownloadModal} title="Select to Download as CSV">
                <CSVModal spot={spot} filters={filters} />
            </Modal>

            {/* PDF Preview */}
            <Modal ref={pdfDownloadModal} title="Download PDF Report">
                <PDFReportPreview
                    spot={spot}
                    filters={filters}
                    onClose={() => pdfDownloadModal.current?.closeModal()}
                />
            </Modal>
        </div>
    );
}

export default DownloadReport;
